import { filter, map, pipe, sort, toArray } from "@fxts/core";
import type { Faction } from "@core/archetypes/faction";
import { relationThresholds } from "@core/components/relations";
import type { RequireComponent } from "@core/tsHelpers";
import { pickRandom } from "@core/utils/generators";
import { entityIndexer } from "@core/entityIndexer/entityIndexer";
import { System } from "../system";
import type { Sim } from "../../sim";

export const spottingRadius = 7;
const cache = new Map<
  string,
  Array<RequireComponent<"hitpoints" | "owner" | "position">>
>();

export class SpottingSystem extends System<"exec"> {
  apply = (sim: Sim) => {
    super.apply(sim);

    sim.hooks.subscribe("phase", ({ phase }) => {
      if (phase === "update") {
        this.exec();
      }
    });
  };

  static getEnemies(
    potentialEnemies: Iterable<
      RequireComponent<"hitpoints" | "owner" | "position">
    >,
    entity: RequireComponent<"owner" | "position">
  ) {
    const entityOwner = entity.sim.getOrThrow<Faction>(entity.cp.owner.id);

    const cacheKey = [
      entity.cp.owner!.id,
      entity.cp.position.sector,
      entity.cp.orders?.value[0]?.type === "pillage" ? "pillage" : "default",
    ].join(":");
    const enemies =
      cache.get(cacheKey) ??
      pipe(
        potentialEnemies,
        filter((e) => {
          const isMiningOnRestrictedArea =
            entityOwner.cp.ai?.restrictions.mining && e.cp.mining?.entityId;
          const isSubjectToPillaging =
            entity.cp.orders?.value[0]?.type === "pillage" &&
            entityOwner.cp.relations.values[e.cp.owner.id]! <= 0 &&
            (e.tags.has("role:transport") || e.tags.has("role:mining")) &&
            (e.cp.dockable?.size === "small" ||
              e.cp.dockable?.size === "medium");
          const isEnemy =
            entityOwner.cp.relations.values[e.cp.owner.id]! <
            relationThresholds.attack;

          return (
            e.cp.owner.id !== entityOwner.id &&
            (isMiningOnRestrictedArea || isSubjectToPillaging || isEnemy)
          );
        }),
        toArray
      );
    if (!cache.get(cacheKey)) {
      cache.set(cacheKey, enemies);
    }

    return pipe(
      enemies,
      map((e) => ({
        entity: e,
        distance: e.cp.position.coord.squaredDistance(entity.cp.position.coord),
      })),
      sort((a, b) => a.distance - b.distance),
      toArray
    );
  }

  exec = (): void => {
    cache.clear();
    if (!this.cooldowns.canUse("exec")) return;

    for (const entity of this.sim.index.orderable.getIt()) {
      const currentOrder = entity.cp.orders.value[0];
      const owner = entity.sim.getOrThrow<Faction>(entity.cp.owner.id);

      if (
        !currentOrder ||
        !(
          ["patrol", "pillage", "escort"].includes(currentOrder?.type) ||
          owner.cp.policies.enemySpotted[
            entity.tags.has("role:military") ? "military" : "civilian"
          ] === "attack"
        ) ||
        currentOrder?.type === "attack" ||
        !entity.cp.owner ||
        ((currentOrder?.type === "patrol" ||
          currentOrder?.type === "pillage") &&
          entity.cp.position.sector !== currentOrder.sectorId)
      )
        continue;

      const enemy = pickRandom(
        SpottingSystem.getEnemies(
          entityIndexer.searchBySector(entity.cp.position.sector, [
            "hitpoints",
            "owner",
            "position",
          ]),
          entity
        ).slice(0, 3)
      );

      if (enemy?.distance <= spottingRadius) {
        currentOrder.interrupt = true;
        entity.cp.orders.value.splice(1, 0, {
          type: "attack",
          ordersForSector: entity.cp.position.sector,
          origin: "auto",
          targetId: enemy.entity.id,
          actions: [],
          followOutsideSector: false,
          maxDistance:
            currentOrder.type === "pillage" ? spottingRadius * 1.1 : undefined,
        });
      }
    }

    this.cooldowns.use("exec", 1 + Math.random());
  };
}

export const spottingSystem = new SpottingSystem();
